import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Crest } from "@/components/Crest";
import { EcgLine } from "@/components/EcgLine";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      login(data.access_token, data.user);
      navigate("/portal");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-ivory" data-testid="login-page">
      <div className="hidden lg:flex lg:w-1/2 bg-navy relative flex-col justify-between p-14 overflow-hidden">
        <Link to="/" className="flex items-center" data-testid="auth-brand-link">
          <Crest light className="h-12" />
        </Link>
        <div>
          <p className="font-serif italic text-3xl text-ivory/90 leading-snug max-w-md">
            “Diagnostics of record, between colleagues of trust.”
          </p>
          <EcgLine className="text-gold/70 h-12 w-72 mt-10" />
        </div>
        <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-ivory/40">
          Closed Network · Verified Clinicians Only
        </p>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 paper-grain">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-md"
        >
          <p className="font-mono text-[11px] tracking-[0.35em] uppercase text-gold mb-3">
            Specialist Portal
          </p>
          <h1 className="font-serif text-3xl font-semibold text-navy mb-8">Sign in</h1>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="font-mono text-[10px] tracking-[0.25em] uppercase text-navy/50 block mb-2">
                Email
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="w-full bg-white border border-navy/15 px-4 py-3 text-sm text-navy focus:outline-none focus:border-gold transition-colors"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] tracking-[0.25em] uppercase text-navy/50 block mb-2">
                Password
              </label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="w-full bg-white border border-navy/15 px-4 py-3 text-sm text-navy focus:outline-none focus:border-gold transition-colors"
              />
            </div>

            {error && (
              <p data-testid="login-error" className="text-sm text-crimson border border-crimson/30 bg-crimson/5 px-4 py-3">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full bg-navy text-ivory font-mono text-xs tracking-[0.25em] uppercase py-4 hover:bg-gold hover:text-navy transition-colors disabled:opacity-50"
            >
              {loading ? "Authenticating…" : "Enter Portal"}
            </button>
          </form>

          <p className="mt-8 text-sm text-navy/50">
            Not yet a member?{" "}
            <Link to="/register" data-testid="login-register-link" className="text-gold hover:text-navy transition-colors">
              Request access
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
